import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ErrorHandler,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  PLATFORM_ID,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import type { MapMarker as NgMapMarker } from '@angular/google-maps';
import { GoogleMapsModule, MapInfoWindow } from '@angular/google-maps';
import { DirectivesModule } from '@freelancer/directives';
import { GoogleMaps } from '@freelancer/google-maps';
import { FreelancerAsyncPipeModule } from '@freelancer/pipes';
import { TimeUtils } from '@freelancer/time-utils';
import {
  FreelancerBreakpointValues,
  FreelancerBreakpoints,
  FreelancerMaxBreakpoint,
} from '@freelancer/ui/breakpoints';
import { isDefined, isEqual } from '@freelancer/utils';
import type { Observable } from 'rxjs';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import type {
  DirectionsDetails,
  MapBounds,
  MapMarker,
  MarkerActivatedEventData,
  MarkerIcon,
  MarkerReferenceId,
  TooltipRef,
} from './map.types';
import { MapCoordinates, MapSize, TooltipBehavior } from './map.types';
import { defaultTheme, hideLandmarksTheme } from './themes';

@Component({
  standalone: true,
  imports: [
    DirectivesModule,
    FreelancerAsyncPipeModule,
    GoogleMapsModule,
    NgTemplateOutlet,
  ],
  selector: 'fl-map',
  template: `
    <!--
    hidden in e2e tests because we don't want to be forced to
    update the tests just because google changed their rendering
    -->
    <div class="MapContainer">
      @if (isBrowser) {
        @if (isMapLoaded) {
          <google-map
            flHideInEndToEndTests
            #map
            height="100%"
            width="100%"
            [attr.data-size]="size"
            [center]="{
              lat: _center?.latitude ?? 0,
              lng: _center?.longitude ?? 0,
            }"
            [options]="mapOptions"
            [zoom]="initialZoom"
            (boundsChanged)="boundsChange()"
            (centerChanged)="centerChange()"
            (mapClick)="closeTooltip()"
            (zoomChanged)="zoomChange()"
          >
            @for (
              marker of markers;
              track trackByMarker(index, marker);
              let index = $index
            ) {
              <map-marker
                #mapMarker="mapMarker"
                [options]="{
                  zIndex: index === 0 ? 2 : 1,
                  icon: marker.icon?.url,
                }"
                [position]="{
                  lat: marker.location.latitude,
                  lng: marker.location.longitude,
                }"
                (mapClick)="markerClick(mapMarker, marker)"
                (mapMouseover)="markerMouseHover(mapMarker, marker)"
                (mapMouseout)="markerMouseOut(mapMarker)"
              >
              </map-marker>
            }
            @if (tooltipTemplate && (isScreenSizeTooltipFriendly$ | flAsync)) {
              <map-info-window
                [options]="tooltipOptions"
                (mouseenter)="tooltipMouseEnter()"
                (mouseleave)="tooltipMouseLeave()"
                (closeclick)="handleTooltipClose()"
              >
                @if (isTooltipReady) {
                  <div class="InfoWindow">
                    <ng-template
                      [ngTemplateOutlet]="tooltipTemplate"
                    ></ng-template>
                  </div>
                }
              </map-info-window>
            }
            @if (directions) {
              <map-directions-renderer
                [directions]="directions"
                [options]="{ suppressMarkers: true }"
              ></map-directions-renderer>
            }
          </google-map>
        }
      } @else {
        <div></div>
      }
    </div>
  `,
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements OnChanges, OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private breakpointObserver = inject(BreakpointObserver);
  private cd = inject(ChangeDetectorRef);
  private errorHandler = inject(ErrorHandler);
  private googleMaps = inject(GoogleMaps);
  private timeUtils = inject(TimeUtils);

  // FIXME: T267853 - don't hardcode this?
  readonly iconUrl =
    'http://cdn5.f-cdn.com/static/css/images/ProjectPin48px.png';
  readonly defaultCustomIconScale = {
    width: 32,
    height: 32,
  };
  readonly defaultIconHeight = 48;
  readonly tooltipArrowOffset = 12;
  readonly tooltipPadding = 16;

  @ViewChild('map')
  map: google.maps.Map;

  @ViewChild(MapInfoWindow)
  tooltip?: MapInfoWindow;

  /**
   * The actual angular components for the google map markers.
   * The naming can be confusing compared to the input `markers` property which represents the marker metadata,
   * but from a client usage perspective it makes sense.
   */
  @ViewChildren('mapMarker')
  mapMarkers?: QueryList<NgMapMarker>;

  @ContentChild('tooltipTemplate')
  tooltipTemplate: TemplateRef<any>;

  /** Plot multiple custom markers onto the map */
  @Input() markers: readonly MapMarker[];

  /** Center the map on a specific location. If provided, prevents map bounds from being adjusted when markers change. */
  @Input() center?: MapCoordinates;

  /** If the user can drag/zoom/click on the map. */
  @Input() interactive = false;
  /** If the map should emit direction details and show route between origin and destination */
  @Input() getDirectionsDetails = false;

  @HostBinding('attr.data-size')
  @Input()
  size = MapSize.DEFAULT;

  @HostBinding('attr.data-size-tablet')
  @Input()
  sizeTablet?: MapSize;

  @HostBinding('attr.data-size-desktop')
  @Input()
  sizeDesktop?: MapSize;
  /** TODO: T267853 - Decide what zoom levels we want to support and put them into an enum.
   * Contact UI Eng if you want to use this. */
  @Input() zoom = 14;
  @Input() zoomMobile?: number;

  /** If the tooltip should be displayed on click or on hover */
  @Input()
  tooltipBehavior: TooltipBehavior = TooltipBehavior.HOVER;

  /** If the tooltip should be hidden on a mobile viewport  */
  @Input()
  hideTooltipOnMobile = false;

  /** If the map should pan tooltip when a tooltip is opened to ensure it is always in view */
  @Input()
  panOnTooltipOpen = false;

  /** Specify if the tooltip's content width (i.e. excluding padding) should be fixed, in px. */
  @Input()
  tooltipContentWidth?: number;

  /** If the map should display landmarks such as restaurants, shops and parks */
  @Input()
  hideLandmarks = false;

  @Output() boundsChanged = new EventEmitter<MapBounds>();
  @Output() centerChanged = new EventEmitter<MapCoordinates>();
  @Output() directionsDetails = new EventEmitter<
    DirectionsDetails | undefined
  >();
  @Output() markerHovered = new EventEmitter<MarkerActivatedEventData>();
  @Output() markerSelected = new EventEmitter<MarkerActivatedEventData>();
  /** Not emitted when a tooltip is "closed" by another one opening. */
  @Output() tooltipClose = new EventEmitter<void>();
  @Output() zoomChanged = new EventEmitter<number>();

  directions: google.maps.DirectionsResult | undefined;
  initialZoom: number;
  isBrowser: boolean;
  isMapLoaded: boolean;
  isScreenSizeTooltipFriendly$: Observable<boolean>;
  // Used to determine when content projection contents are ready to be displayed
  // Otherwise, since the tooltip is initialised before it is opened, the content may not be ready
  isTooltipReady = false;
  mapOptions: google.maps.MapOptions;
  theme: google.maps.MapTypeStyle[];
  tooltipOptions: google.maps.InfoWindowOptions;

  /** Separate to the input to distinguish between automatically calculated coordinates and manually provided ones */
  _center?: MapCoordinates;

  private hoveredMapMarker: NgMapMarker | undefined;
  private subscriptions = new Subscription();
  private tooltipHovered = false;
  private _bounds$ = new Subject<MapBounds>();

  async ngOnInit(): Promise<void> {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.isMapLoaded = await this.googleMaps.load();

    this.isScreenSizeTooltipFriendly$ = this.breakpointObserver
      .observe(FreelancerMaxBreakpoint.TABLET)
      .pipe(
        map(
          breakpointState =>
            !(breakpointState.matches && this.hideTooltipOnMobile),
        ),
      );

    this.theme = this.hideLandmarks
      ? hideLandmarksTheme.concat()
      : defaultTheme.concat();

    this._center =
      !isDefined(this.center) && this.markers.length === 1
        ? this.markers[0].location
        : this.center;

    this.mapOptions = {
      clickableIcons: this.interactive,
      disableDefaultUI: true,
      draggable: this.interactive,
      gestureHandling: 'greedy',
      styles: this.theme,
      zoomControl: this.interactive,
    };

    const tooltipWidthOptions = this.tooltipContentWidth
      ? {
          maxWidth: this.tooltipContentWidth + 2 * this.tooltipPadding,
          minWidth: this.tooltipContentWidth + 2 * this.tooltipPadding,
        }
      : {};
    this.tooltipOptions = {
      disableAutoPan: !this.panOnTooltipOpen,
      ...tooltipWidthOptions,
    };

    this.initialZoom = this.getInitialZoom();

    if (this.getDirectionsDetails) {
      await this.updateAndEmitDirections();
    }

    /**
     * There are 2 reasons why another change detection cycle is needed here:
     * 1. Since the map is hidden until the maps API is loaded here in the `OnInit` hook,
     *    it will remain hidden if there is no CD cycle afterwards.
     *    This can be resolved by using only `markForCheck` instead of `detectChanges`.
     * 2. The google.maps.Map viewChild is not initialised yet, and due to the async nature of loading,
     *    it does not necessarily occur before the `AfterViewInit` hook either.
     *    Force change detection to guarantee the map is available to make bounds adjustments if required.
     */
    // eslint-disable-next-line local-rules/no-detectchanges-without-detach-reattach
    this.cd.detectChanges();

    if (this.markers.length > 1 && !isDefined(this.center)) {
      this.fitBoundsToMarkers();
    }

    this.subscriptions.add(
      this._bounds$
        .pipe(this.timeUtils.rxDebounceTime(500), distinctUntilChanged(isEqual))
        .subscribe(bounds => this.boundsChanged.emit(bounds)),
    );
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if ('markers' in changes) {
      this.markers = this.markers.map(customMarker => ({
        ...customMarker,
        icon: this.transformIcon(customMarker.icon),
      }));

      //  Manually trigger the opening of tooltip when action is not from clicking of map pin.
      const markerTooltipToOpen = this.markers.find(
        marker => marker.showTooltip,
      );
      if (markerTooltipToOpen) {
        const mapMarker = this.mapMarkers?.find(marker => {
          const position = marker.getPosition();
          return (
            position?.lat() === markerTooltipToOpen.location.latitude &&
            position?.lng() === markerTooltipToOpen.location.longitude
          );
        });
        if (mapMarker) {
          // Since the actions to perform are the same as a marker click anyway, simulate a click
          this.markerClick(mapMarker, markerTooltipToOpen, true);
        } else {
          this.errorHandler.handleError(
            'Could not find corresponding MapMarker element to open tooltip.',
          );
        }
      }
    }

    if ('center' in changes) {
      this._center =
        !isDefined(this.center) && this.markers.length === 1
          ? this.markers[0].location
          : this.center;
    }

    if ('interactive' in changes || 'hideLandmarks' in changes) {
      this.theme = this.hideLandmarks
        ? hideLandmarksTheme.concat()
        : defaultTheme.concat();

      this.mapOptions = {
        ...this.mapOptions,
        clickableIcons: this.interactive,
        draggable: this.interactive,
        styles: this.theme,
        zoomControl: this.interactive,
      };
    }

    if ('panOnTooltipOpen' in changes || 'tooltipContentWidth' in changes) {
      const tooltipWidthOptions = this.tooltipContentWidth
        ? {
            maxWidth: this.tooltipContentWidth + 2 * this.tooltipPadding,
            minWidth: this.tooltipContentWidth + 2 * this.tooltipPadding,
          }
        : {};
      this.tooltipOptions = {
        disableAutoPan: !this.panOnTooltipOpen,
        ...tooltipWidthOptions,
      };
    }

    if (
      'markers' in changes &&
      changes.markers.previousValue?.length !==
        changes.markers.currentValue.length &&
      this.markers.length > 1 &&
      !isDefined(this.center)
    ) {
      this.fitBoundsToMarkers();
    }

    if ('zoom' in changes && this.zoom) {
      this.initialZoom = this.getInitialZoom();
    }

    if (this.getDirectionsDetails) {
      this.updateAndEmitDirections();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private transformIcon(icon?: MarkerIcon): MarkerIcon {
    const iconScale =
      icon && icon.scaledSize ? icon.scaledSize : this.defaultCustomIconScale;
    return icon ? { ...icon, scaledSize: iconScale } : { url: this.iconUrl };
  }

  tooltipMouseEnter(): void {
    this.tooltipHovered = true;
  }

  tooltipMouseLeave(): void {
    this.tooltipHovered = false;
    if (this.tooltipBehavior === TooltipBehavior.HOVER) {
      this.closeTooltip();
    }
  }

  markerClick(
    marker: NgMapMarker,
    markerData: MapMarker,
    forceOpenTooltip = false,
  ): void {
    const tooltip = this.buildTooltip(marker, markerData);

    if (forceOpenTooltip) {
      tooltip.openTooltip();
    }

    this.markerSelected.emit({
      tooltip,
      screenSize: this.getScreenSize(),
    });
  }

  markerMouseHover(marker: NgMapMarker, markerData: MapMarker): void {
    const tooltip = this.buildTooltip(marker, markerData);
    this.hoveredMapMarker = marker;

    this.markerHovered.emit({
      tooltip,
      screenSize: this.getScreenSize(),
    });
  }

  buildTooltip(marker: NgMapMarker, markerData: MapMarker): TooltipRef {
    return {
      ...markerData,
      closeTooltip: () => {
        this.closeTooltip();
      },
      openTooltip: () => {
        this.isTooltipReady = true;
        this.tooltip?.open(marker);
      },
    };
  }

  markerMouseOut(marker: NgMapMarker): void {
    if (this.tooltipBehavior === TooltipBehavior.HOVER) {
      // Set a small timeout, otherwise the tooltip will be closed immediately before
      // the hover event is triggered on the tooltip itself.
      this.timeUtils.setTimeout(() => {
        const oldHoveredPosition = this.hoveredMapMarker?.getPosition();
        const newHoveredPosition = marker.getPosition();
        if (
          isDefined(oldHoveredPosition) &&
          isDefined(newHoveredPosition) &&
          !oldHoveredPosition.equals(newHoveredPosition)
        ) {
          // The user has moved the mouse to another marker, don't close the tooltip
          return;
        }

        if (!this.tooltipHovered) {
          this.closeTooltip();
        }
      }, 50);
    }
  }

  trackByMarker(_: number, marker: MapMarker): MarkerReferenceId | MapMarker {
    return marker.referenceId ?? marker;
  }

  getScreenSize(): FreelancerBreakpointValues {
    const isDesktop = this.breakpointObserver.isMatched(
      FreelancerBreakpoints.DESKTOP_SMALL,
    );
    const isTablet = this.breakpointObserver.isMatched(
      FreelancerBreakpoints.TABLET,
    );

    return isDesktop
      ? FreelancerBreakpointValues.DESKTOP_SMALL
      : isTablet
        ? FreelancerBreakpointValues.TABLET
        : FreelancerBreakpointValues.MOBILE;
  }

  boundsChange(): void {
    const bounds = this.map.getBounds();
    if (!bounds) {
      return;
    }
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    this._bounds$.next({
      topRight: { latitude: northEast.lat(), longitude: northEast.lng() },
      bottomLeft: { latitude: southWest.lat(), longitude: southWest.lng() },
    });
  }

  getInitialZoom(): number {
    const screenSize = this.getScreenSize();

    switch (screenSize) {
      case FreelancerBreakpointValues.MOBILE:
        return this.zoomMobile ?? this.zoom;
      default:
        return this.zoom;
    }
  }

  handleTooltipClose(): void {
    this.tooltipClose.emit();
  }

  closeTooltip(): void {
    // Use `isTooltipReady` to determine whether tooltip is open to
    // avoid emitting tooltipClose event if it's already closed.
    if (this.isTooltipReady) {
      this.tooltip?.close();
      this.tooltipHovered = false;
      this.isTooltipReady = false;
      this.tooltipClose.emit();
    }
  }

  centerChange(): void {
    const center = this.map.getCenter();
    if (!center) {
      return;
    }

    const coordinates: MapCoordinates = {
      latitude: center.lat(),
      longitude: center.lng(),
    };
    this.centerChanged.emit(coordinates);
  }

  zoomChange(): void {
    const zoomLevel = this.map.getZoom();
    if (!zoomLevel) {
      return;
    }
    this.zoomChanged.emit(zoomLevel);
  }

  private fitBoundsToMarkers(): void {
    if (!this.isMapLoaded || !this.map) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    this.markers.forEach(marker => {
      bounds.extend({
        lat: marker.location.latitude,
        lng: marker.location.longitude,
      });
    });
    this.map.fitBounds(bounds);
  }

  // TODO: T308351
  // Plans to update to NHVR endpoints after NHVR POC completion
  private async updateAndEmitDirections(): Promise<void> {
    this.directions = await this.googleMaps.getDirections(
      this.markers[0]?.location,
      this.markers[1]?.location,
    );

    const leg = this.directions?.routes[0]?.legs[0];
    const [distance, duration] = [leg?.distance?.value, leg?.duration?.value];

    this.directionsDetails.emit(
      this.directions?.routes.length !== 0 && distance && duration
        ? {
            distance,
            duration,
          }
        : undefined,
    );
  }

  // TODO: T267853 - handle longpress for touchscreens
  // On mobile, the tooltip is displayed when the user longpresses the element
  // and hides after a delay of 1500ms.
}
